import { deepmerge } from '@mui/utils';
import type { Theme, ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { DEFAULTCOLOR_TEXT_DISABLED } from '../default/palette';
import type { Device } from '../default';
import { generateDefaultThemeOptions } from '../default';
import { fonts } from './fonts';

const COLOR_PRIMARY_MAIN = '#EB001F';
const COLOR_SECONDARY_MAIN = '#ff0000';
const DEFAULTCOLOR_TEXT_DARK = '#222222';
const DEFAULTCOLOR_TEXT_LIGHT = '#ffffff';
const DEFAULTCOLOR_TEXT_TRANSPARENT_DARK = 'rgba(34, 34, 34, 0.65)';
const DEFAULTCOLOR_TEXT_TRANSPARENT_LIGHT = 'rgba(255, 255, 255, 0.65)';
const DEFAULTCOLOR_COMMON_LIGHT = '#f6f6f6';

/** Define theme object.
 * See here for more info: https://mui.com/customization/theming/ */
const themeOptions: ThemeOptions = {
  // https://mui.com/customization/palette/
  palette: {
    primary: {
      main: COLOR_PRIMARY_MAIN,
      textTransparent: DEFAULTCOLOR_TEXT_TRANSPARENT_LIGHT,
      contrastText: DEFAULTCOLOR_TEXT_LIGHT,
    },
    secondary: {
      main: COLOR_SECONDARY_MAIN,
    },
    grey: {
      main: '#e6e6e6',
      light: DEFAULTCOLOR_COMMON_LIGHT,
      dark: '#777777',
    },
    text: {
      disabled: DEFAULTCOLOR_TEXT_DISABLED,
      primary: DEFAULTCOLOR_TEXT_DARK,
      secondary: DEFAULTCOLOR_TEXT_TRANSPARENT_DARK,
      dark: DEFAULTCOLOR_TEXT_DARK,
      darkTransparent: DEFAULTCOLOR_TEXT_TRANSPARENT_DARK,
      light: DEFAULTCOLOR_TEXT_LIGHT,
      lightTransparent: DEFAULTCOLOR_TEXT_TRANSPARENT_LIGHT,
    },
    sale: {
      main: '#eb001f',
    },
    footer: {
      main: DEFAULTCOLOR_COMMON_LIGHT,
      contrastText: DEFAULTCOLOR_TEXT_DARK,
    },
  },
  typography: {
    fontFamily: fonts.primary.style.fontFamily,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightBold: 700,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: '',
    },
    MuiButton: {
      styleOverrides: {
        root: ({
          theme: {
            unstable_sx: sx,
            palette: { grey },
          },
        }) =>
          sx({
            ':disabled': { bgcolor: grey.main, color: grey.dark },
          }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: () => ({
          '&.price-strike-through.allow-strikethrough': {
            textDecoration: 'none',
          },
        }),
      },
    },
  },
};

/** Merge with default theme options and create a theme instance
 * See here for more info: https://mui.com/customization/theming/#api */
const themes: Record<Device, Theme | undefined> = {
  mobile: undefined,
  tablet: undefined,
  desktop: undefined,
};

const theme = (device: Device = 'desktop') => {
  if (!themes[device]) {
    themes[device] = createTheme(deepmerge(generateDefaultThemeOptions(device), themeOptions));
  }

  return themes[device];
};

// eslint-disable-next-line import-x/no-default-export
export default theme;
